import { useState } from "react";
import { LIMITS, type GroupState } from "@psplit/ledger-core";
import { repo } from "../lib/repo.js";
import { announceRotation, syncGroup } from "../lib/relaySync.js";
import { errorMessage } from "../lib/errors.js";

/**
 * Has anyone left or been removed since the key last changed? That is the
 * moment the old key is doing damage, so it is the moment to say something.
 * Computed from topo order rather than timestamps, which are advisory here.
 */
function departureSinceRotation(groupId: string): boolean {
  let departed = false;
  for (const op of repo.storeOf(groupId).topoOrder()) {
    if (op.kind === "member_leave" || op.kind === "member_remove") departed = true;
    if (op.kind === "key_rotate") departed = false;
  }
  return departed;
}

export function MembershipCard({
  id,
  state,
  me,
}: {
  id: string;
  state: GroupState;
  me: string;
}) {
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState("");
  const [confirming, setConfirming] = useState("");
  const [reason, setReason] = useState("");

  const isMember = repo.membership(id) === "member";
  const epoch = repo.epoch(id);
  const others = state.members.filter((m) => m.active && !m.isGhost && m.pubKey !== me);
  const shouldRotate = departureSinceRotation(id);

  const remove = async (member: string) => {
    setBusy(member);
    setStatus("");
    try {
      await repo.removeMember(id, member, reason.trim());
      await syncGroup(id);
      setConfirming("");
      setReason("");
      setStatus("Removed. They cannot write to the group any more. Change the key to stop them reading what comes next.");
    } catch (e) {
      setStatus(errorMessage(e));
    } finally {
      setBusy("");
    }
  };

  const rotate = async () => {
    setBusy("rotate");
    setStatus("");
    try {
      const { epoch: next, previousKey } = await repo.rotateKey(id);
      // Post the rotation where anyone still on the old mailbox will see it,
      // BEFORE syncing forward, so a member who is offline can catch up later.
      await announceRotation(id, previousKey);
      await syncGroup(id);
      const staying = state.members.filter((m) => m.active && !m.isGhost).length;
      setStatus(
        staying === 1
          ? `Key changed. You are the only person who can read what the group writes from here (key ${next}).`
          : `Key changed. New entries are readable only by the ${staying} people in the group now (key ${next}).`,
      );
    } catch (e) {
      setStatus(errorMessage(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="card">
      <h3>Who is in this group</h3>
      <p className="fineprint">
        Anyone here can remove anyone else: there is no owner. Every removal is signed and stays
        in the ledger with the name of whoever did it, so it is visible rather than preventable.
      </p>
      {others.length === 0 && <p className="fineprint">Nobody else is in the group yet.</p>}
      {others.map((m) => (
        <div className="row gap wrap" key={m.pubKey}>
          <span>{m.name}</span>
          {confirming === m.pubKey ? (
            <>
              <input
                value={reason}
                maxLength={LIMITS.reason}
                placeholder="why (optional, everyone sees it)"
                onChange={(e) => setReason(e.target.value)}
              />
              <button
                className="btn small danger"
                disabled={busy !== ""}
                onClick={() => void remove(m.pubKey)}
              >
                Confirm remove
              </button>
              <button className="btn small" onClick={() => setConfirming("")}>
                Cancel
              </button>
            </>
          ) : (
            <button
              className="btn small danger"
              disabled={!isMember || busy !== ""}
              onClick={() => {
                setConfirming(m.pubKey);
                setReason("");
              }}
            >
              Remove
            </button>
          )}
        </div>
      ))}

      <h4>The group key</h4>
      {shouldRotate && (
        <div className="notice">
          Someone has left or been removed since the key last changed. Until you change it, they
          can still read everything the group writes from here on.
        </div>
      )}
      <p className="fineprint">
        Changing the key locks out anyone who is no longer in the group, and anyone holding an old
        invite link. It does <strong>not</strong> unsend the past: whoever had the old key keeps
        every entry written under it. Everyone still in the group gets the new key automatically
        the next time they sync, but a phone that stays offline for more than a month may need a
        fresh invite.
      </p>
      <button className="btn" disabled={!isMember || busy !== ""} onClick={() => void rotate()}>
        {busy === "rotate" ? "changing…" : epoch === 0 ? "Change the group key" : `Change the group key (now on key ${epoch})`}
      </button>
      {state.removals.length > 0 && (
        <details className="adv">
          <summary>Removals ({state.removals.length})</summary>
          {state.removals.map((r, i) => (
            <p className="fineprint" key={`${r.member}-${i}`}>
              {nameOf(state, r.member)} removed by {nameOf(state, r.by)}
              {r.reason === "" ? "" : `: ${r.reason}`}
            </p>
          ))}
        </details>
      )}
      {status !== "" && <div className="notice">{status}</div>}
    </div>
  );
}

function nameOf(state: GroupState, pub: string): string {
  return state.members.find((m) => m.pubKey === pub)?.name ?? pub.slice(0, 8);
}
